import { Button, Dialog, MultiSelect, type MultiSelectOption } from '@eppendorf/vnls-react-components';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Loader } from '$components/loader/loader';
import { useGetReports } from '$features/organizations';

interface ReportsProps {
  show: boolean;
  setShow: (status: boolean) => void;
}

export function Reporting({ show, setShow }: ReportsProps) {
  const ref = useRef<HTMLElement | null>(null);
  const { t } = useTranslation();
  const [selectedFilters, setSelectedFilters] = useState<MultiSelectOption[]>([]);
  const options = useMemo(
    () => [
      { value: 'organizations', label: 'Organizations' },
      { value: 'devices', label: 'Devices' },
      { value: 'users', label: 'Users' },
      { value: 'notifications', label: 'Notifications' },
      { value: 'aiChatHistory', label: 'AI Chat History' },
      { value: 'icingPrediction', label: 'Icing Prediction' },
      { value: 'auditLogs', label: 'System logs' },
    ],
    [],
  );
  const { mutateAsync: getReports, isPending } = useGetReports(selectedFilters.map(({ value }) => value));

  const handleOnReportsRequest = async () => {
    try {
      await getReports();
      toast('Report requested. A download link will be emailed to you when it is ready.', { type: 'success' });
      setShow(false);
    } catch (error) {
      // eslint-disable-next-line no-console -- for debugging
      console.error(error);
      toast('Unable to request reports. Try again later.', { type: 'error' });
    }
  };

  return (
    <Dialog isOpen={show} onOpenChange={(open) => setShow(open)}>
      <Dialog.Title>{t('shared.reports')}</Dialog.Title>
      <div style={{ minHeight: 300 }}>
        <MultiSelect
          className="reports-filters"
          value={selectedFilters}
          options={options}
          ariaLabelledBy="reports-filters"
          placeholder="Select filters..."
          onChange={(x) => setSelectedFilters(x)}
          ref={ref}
        />
      </div>
      <Dialog.Actions className="m-top-xxl">
        {isPending && <Loader />}
        <Button className="btn--solid" disabled={!selectedFilters.length || isPending} onClick={handleOnReportsRequest}>
          {isPending ? 'Requesting reports...' : 'Request reports'}
        </Button>
        <Dialog.Close>
          <Button className="btn--solid">{t('shared.close')}</Button>
        </Dialog.Close>
      </Dialog.Actions>
    </Dialog>
  );
}
